import { ReadingsDataRawResponse } from "@inrange/building-manager-api-client";
import { Col, Row, Table } from "react-bootstrap";
import { Formatters } from "./types";

const UpdatedReadingsRaw = ({
  data,
  startDate,
  endDate,
  formatters,
}: {
  data: ReadingsDataRawResponse;
  startDate: string;
  endDate: string;
  formatters: Formatters;
}) => {
  return (
    <>
      <Row className="mt-3">
        <Col>
          <h5>Raw Readings Summary</h5>
          <p>
            <strong>Date Range:</strong> {data.start} to {data.end}
          </p>
          <p>
            <strong>Total Series:</strong> {data.series.length}
          </p>
          {data.history.length > 0 && (
            <>
              <p>
                <strong>Readings with Updates:</strong> {data.history.length}
              </p>
              {(() => {
                const history = data.history;

                // Pre-calculate timestamp bounds for efficient filtering
                const { startMs, endMs } =
                  startDate && endDate
                    ? formatters.dateRangeToTimestamps(startDate, endDate)
                    : { startMs: 0, endMs: Infinity };

                // Filter history by date range if set
                const filteredHistory = history.filter((h) => {
                  if (startDate && endDate) {
                    const pointMs = new Date(h.readingTimestamp).getTime();
                    return pointMs >= startMs && pointMs <= endMs;
                  }
                  return true;
                });

                if (filteredHistory.length === 0) {
                  return null;
                }

                // Sort by timestamp to find first and last
                const sortedHistory = [...filteredHistory].sort(
                  (a, b) =>
                    new Date(a.readingTimestamp).getTime() -
                    new Date(b.readingTimestamp).getTime()
                );

                const firstUpdate = sortedHistory[0];
                const lastUpdate = sortedHistory[sortedHistory.length - 1];

                // Get most recent 10 updates (sorted by timestamp descending)
                const recentUpdates = [...filteredHistory]
                  .sort(
                    (a, b) =>
                      new Date(b.readingTimestamp).getTime() -
                      new Date(a.readingTimestamp).getTime()
                  )
                  .slice(0, 10);

                return (
                  <>
                    <p>
                      <strong>First Update:</strong>{" "}
                      {formatters.toLocaleString(firstUpdate.readingTimestamp)}
                    </p>
                    <p>
                      <strong>Last Update:</strong>{" "}
                      {formatters.toLocaleString(lastUpdate.readingTimestamp)}
                    </p>
                    <h6 className="mt-3">Most Recent 10 Updated Readings</h6>
                    <div
                      style={{
                        maxHeight: "500px",
                        overflowY: "auto",
                      }}
                    >
                      <Table striped bordered hover size="sm" responsive>
                        <thead
                          style={{
                            position: "sticky",
                            top: 0,
                            background: "white",
                          }}
                        >
                          <tr>
                            <th>Reading Timestamp</th>
                            <th>Hardware Type</th>
                            <th>Serial Number</th>
                            <th>Reading Name</th>
                            <th>History (Oldest → Current)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentUpdates.map((update, index) => (
                            <tr key={index}>
                              <td>
                                {formatters.toLocaleString(
                                  update.readingTimestamp
                                )}
                              </td>
                              <td>{update.hardwareType}</td>
                              <td>
                                <code>{update.serialNumber}</code>
                              </td>
                              <td>{update.readingName}</td>
                              <td>
                                {update.versions.map((val, idx) => (
                                  <div
                                    key={idx}
                                    style={{
                                      marginBottom:
                                        idx < update.versions.length - 1
                                          ? "4px"
                                          : 0,
                                    }}
                                  >
                                    <strong>
                                      {formatters.toLocaleString(
                                        val.ingestionTimestamp
                                      )}
                                      :
                                    </strong>{" "}
                                    {val.reading.toFixed(3)}{" "}
                                    {val.source && (
                                      <span className="text-muted">
                                        ({val.source})
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </>
                );
              })()}
            </>
          )}
        </Col>
      </Row>
    </>
  );
};

export default UpdatedReadingsRaw;
